import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Star, QrCode, Download, MessageCircle, CheckCircle, XCircle, Trash2, Eye, Copy, ExternalLink, Send, Sparkles, FileText, UserPlus, MonitorSmartphone,
} from "lucide-react";
import ManualReflectionDialog from "@/components/facilitator/ManualReflectionDialog";
import ManualFeedbackDialog from "@/components/facilitator/ManualFeedbackDialog";

const STATUS_COLOR = {
  pending: "bg-[#D4A547]/20 text-[#A6741F]",
  approved: "bg-[#6B8E5A] text-white",
  rejected: "bg-[#A23E3E]/15 text-[#A23E3E]",
};

const WA_STATUS_COLOR = {
  delivered: "bg-[#6B8E5A] text-white",
  read: "bg-[#6B8E5A] text-white",
  sent: "bg-[#D4A547]/20 text-[#A6741F]",
  queued: "bg-[#D4A547]/20 text-[#A6741F]",
  sending: "bg-[#D4A547]/20 text-[#A6741F]",
  accepted: "bg-[#D4A547]/20 text-[#A6741F]",
  failed: "bg-[#A23E3E]/15 text-[#A23E3E]",
  undelivered: "bg-[#A23E3E]/15 text-[#A23E3E]",
  skipped: "bg-[#6B5944]/15 text-[#6B5944]",
  pending: "bg-[#D4A547]/15 text-[#A6741F]",
};

const SITE_BASE = window.location.origin;

const downloadSvgAsPng = async (svgEl, filename) => {
  if (!svgEl) return;
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svg64 = btoa(unescape(encodeURIComponent(svgData)));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    const scale = 4;
    canvas.width = (svgEl.clientWidth || 256) * scale;
    canvas.height = (svgEl.clientHeight || 256) * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };
  img.src = "data:image/svg+xml;base64," + svg64;
};

const QRTile = ({ url, label, sublabel, testid, kioskUrl }) => {
  const id = `qr-${(testid || label).replace(/[^a-z0-9]/gi, "")}`;
  return (
    <div className="ccs-card p-5 text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{sublabel}</div>
      <div className="font-display text-base font-semibold text-[#2A1810] mt-1">{label}</div>
      <div className="mt-4 inline-block p-3 bg-white border-2 border-[#C28A2E]/40 rounded-xl">
        <QRCodeSVG id={id} value={url} size={160} level="M" fgColor="#2A1810" bgColor="#FFFFFF" />
      </div>
      <div className="text-[10px] text-[#6B5944] mt-3 break-all font-mono">{url}</div>
      <div className="flex flex-wrap justify-center gap-1 mt-3">
        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} data-testid={`copy-${testid}`}>
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Link
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.open(url, "_blank")} data-testid={`open-${testid}`}>
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadSvgAsPng(document.getElementById(id), `${label.replace(/\s+/g, "-")}.png`)} data-testid={`download-${testid}`}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> PNG
        </Button>
      </div>
      {kioskUrl && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(kioskUrl, "_blank")}
          className="w-full mt-2 border-[#C28A2E]/40 text-[#5D3A1F] hover:bg-[#C28A2E]/10"
          data-testid={`kiosk-${testid}`}
        >
          <MonitorSmartphone className="w-3.5 h-3.5 mr-1.5" /> Launch kiosk on this device
        </Button>
      )}
    </div>
  );
};

const ReflectionDetail = ({ r, onClose }) => (
  <Dialog open={!!r} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reflection-detail">
      {r && (
        <>
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Session {r.session_number} · {r.organisation}</div>
            <DialogTitle className="font-display text-2xl">{r.full_name}</DialogTitle>
            <div className="text-sm text-[#6B5944] flex items-center gap-3 mt-1 flex-wrap">
              <span>{r.department}</span>
              <span>·</span>
              <span>{r.whatsapp_number}</span>
              <Badge className={`${WA_STATUS_COLOR[r.whatsapp_status] || ""} text-[10px]`}>
                <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp: {r.whatsapp_status}
              </Badge>
              {r.is_first_time_participant && (
                <Badge className="bg-[#C28A2E]/15 text-[#A6741F] text-[10px]" data-testid="reflection-welcomed-badge">
                  ✨ Welcome sent · {r.welcome_status || "—"}
                </Badge>
              )}
            </div>
          </DialogHeader>
          {[
            ["📌 Most important learning", r.q_most_important],
            ["🎯 Will apply this week", r.q_apply_this_week],
            ["✨ One thing I'll do differently", r.q_do_differently],
            ["💭 Questions or thoughts", r.q_questions_thoughts],
          ].map(([label, val]) => (
            <div key={label} className="mt-4">
              <div className="text-xs font-bold text-[#5D3A1F] uppercase tracking-wider">{label}</div>
              <div className="text-sm text-[#2A1810] mt-1.5 bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2] whitespace-pre-wrap">
                {val || "(no response)"}
              </div>
            </div>
          ))}
          {r.whatsapp_error && (
            <div className="mt-4 text-xs text-[#A23E3E] bg-[#A23E3E]/10 p-3 rounded-lg">
              <strong>WhatsApp error:</strong> {r.whatsapp_error}
            </div>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);

const ModerateDialog = ({ item, onClose, onRefresh }) => {
  const [response, setResponse] = useState(item?.facilitator_response || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setResponse(item?.facilitator_response || ""); }, [item]);

  const act = async (status) => {
    setSaving(true);
    try {
      await api.put(`/feedback/${item.id}/moderate`, { status, facilitator_response: response });
      toast.success(`Marked as ${status}`);
      onRefresh();
      onClose();
    } catch (e) {
      toast.error("Action failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="moderate-dialog">
        {item && (
          <>
            <DialogHeader>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">{item.organisation}</div>
              <DialogTitle className="font-display text-2xl">{item.display_name}</DialogTitle>
              <div className="flex items-center gap-1 mt-1">
                {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= item.star_rating ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`} />)}
                <Badge className="ml-2 bg-[#C28A2E]/15 text-[#A6741F] text-[10px]">{item.would_recommend}</Badge>
                {item.share_publicly ? (
                  <Badge className="bg-[#6B8E5A]/15 text-[#6B8E5A] text-[10px]">Public OK</Badge>
                ) : (
                  <Badge className="bg-[#A23E3E]/15 text-[#A23E3E] text-[10px]">Private only</Badge>
                )}
              </div>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              {item.enjoyed_most && <div><div className="text-xs font-bold text-[#5D3A1F] uppercase">Enjoyed most</div><div className="text-sm bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2] mt-1">{item.enjoyed_most}</div></div>}
              {item.what_changed && <div><div className="text-xs font-bold text-[#5D3A1F] uppercase">What changed</div><div className="text-sm bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2] mt-1">{item.what_changed}</div></div>}
              {item.additional_comments && <div><div className="text-xs font-bold text-[#5D3A1F] uppercase">Comments</div><div className="text-sm bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2] mt-1">{item.additional_comments}</div></div>}
              <div className="space-y-2 pt-2 border-t border-[#EBE3D2]">
                <div className="text-xs font-bold text-[#C28A2E] uppercase tracking-wider">Your response (optional — shown on testimonial wall)</div>
                <Textarea rows={3} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="A short note from Joylance to acknowledge this participant…" className="rounded-lg" data-testid="moderate-response-input" />
              </div>
            </div>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button variant="outline" disabled={saving} onClick={() => act("rejected")} className="text-[#A23E3E] border-[#A23E3E]/30" data-testid="moderate-reject">
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
              <Button disabled={saving || !item.share_publicly} onClick={() => act("approved")} className="bg-[#6B8E5A] hover:bg-[#5a7849]" data-testid="moderate-approve">
                <CheckCircle className="w-4 h-4 mr-1.5" /> {item.share_publicly ? "Approve for wall" : "Cannot publish (private)"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FacilitatorPage = () => {
  const [workshops, setWorkshops] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [waStatus, setWaStatus] = useState({ configured: false });
  const [activeWorkshopId, setActiveWorkshopId] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailR, setDetailR] = useState(null);
  const [moderate, setModerate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportContent, setReportContent] = useState("");
  const [reportMeta, setReportMeta] = useState(null);
  const [manualReflectionOpen, setManualReflectionOpen] = useState(false);
  const [manualFeedbackOpen, setManualFeedbackOpen] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/workshops"),
      api.get("/reflections"),
      api.get("/feedback"),
      api.get("/whatsapp/status"),
    ]).then(([w, r, f, s]) => {
      setWorkshops(w.data);
      setReflections(r.data);
      setFeedback(f.data);
      setWaStatus(s.data);
      if (!activeWorkshopId && w.data[0]) setActiveWorkshopId(w.data[0].id);
    }).finally(() => setLoading(false));
  };
  useEffect(fetchAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeWorkshop = workshops.find((w) => w.id === activeWorkshopId);

  const filteredReflections = reflections.filter((r) =>
    !activeWorkshopId || r.workshop_id === activeWorkshopId
  );
  const filteredFeedback = feedback.filter((f) => {
    if (filterStatus !== "all" && f.status !== filterStatus) return false;
    return true;
  });

  const exportReflectionsCSV = () => {
    const cols = ["created_at", "session_number", "department", "full_name", "whatsapp_number", "whatsapp_status", "q_most_important", "q_apply_this_week", "q_do_differently", "q_questions_thoughts"];
    const header = cols.join(",");
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\n/g, " ")}"`;
    const rows = filteredReflections.map((r) => cols.map((c) => escape(r[c])).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reflections-${activeWorkshop?.name || "all"}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const onDeleteReflection = async (id) => {
    if (!window.confirm("Delete this reflection?")) return;
    await api.delete(`/reflections/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const onDeleteFeedback = async (id) => {
    if (!window.confirm("Delete this feedback entirely?")) return;
    await api.delete(`/feedback/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const generateReport = async () => {
    if (!activeWorkshopId) { toast.error("Select a workshop first"); return; }
    setReportOpen(true);
    setReportLoading(true);
    setReportContent("");
    setReportMeta(null);
    try {
      const res = await api.post("/ai/training-report", { workshop_id: activeWorkshopId });
      setReportContent(res.data.content);
      setReportMeta({
        workshop_name: res.data.workshop_name,
        client_name: res.data.client_name,
        reflections_count: res.data.reflections_count,
        feedback_count: res.data.feedback_count,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setReportContent(`⚠ Unable to generate report.\n\n${msg}`);
    } finally { setReportLoading(false); }
  };

  const downloadReport = () => {
    const blob = new Blob([reportContent], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `post-training-report-${(reportMeta?.workshop_name || "workshop").replace(/\s+/g, "-")}-${new Date().toISOString().slice(0,10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  const pendingCount = feedback.filter((f) => f.status === "pending").length;
  const totalReflections = reflections.length;
  const sessionCounts = [1,2,3,4].map((n) => filteredReflections.filter((r) => r.session_number === n).length);

  return (
    <div className="space-y-6" data-testid="facilitator-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Facilitator</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Sessions, Reflections & Testimonials</h1>
          <p className="text-[#6B5944] mt-1.5">Per-session QR codes, reflection submissions, and the public testimonial wall.</p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Badge className={waStatus.configured ? "bg-[#6B8E5A] text-white" : "bg-[#D4A547]/20 text-[#A6741F]"} data-testid="wa-status-badge">
            <MessageCircle className="w-3 h-3 mr-1.5" /> WhatsApp {waStatus.configured ? "Connected" : "Not configured"}
          </Badge>
          {!waStatus.configured && (
            <div className="text-[10px] text-[#6B5944] max-w-[260px] text-right">
              Add Twilio creds in backend/.env to enable automated WhatsApp recaps.
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp opt-in banner — for facilitator to share with participants */}
      {waStatus.is_sandbox && waStatus.configured && (
        <div className="ccs-card p-5 lg:p-6 ccs-grain border-l-4 border-l-[#C28A2E]" data-testid="sandbox-banner">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
                <MessageCircle className="w-3 h-3" /> Read this to participants at the start of Session 1
              </div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">WhatsApp Opt-in (one-time, 10 seconds)</h3>
              <p className="text-sm text-[#5D3A1F] mt-2 leading-relaxed">
                "To receive your reflection summary on WhatsApp, save this number{" "}
                <strong className="font-mono bg-white px-2 py-0.5 rounded border border-[#EBE3D2]">{waStatus.from_number_display || "+1 415 523 8886"}</strong>{" "}
                and send it the message:{" "}
                {waStatus.sandbox_join_code ? (
                  <strong className="font-mono bg-[#C28A2E] text-white px-2 py-0.5 rounded">join {waStatus.sandbox_join_code}</strong>
                ) : (
                  <span className="font-mono bg-[#A23E3E]/10 text-[#A23E3E] px-2 py-0.5 rounded text-xs">⚠ join code not configured yet</span>
                )}
                . You'll receive a confirmation, then every reflection you submit lands on your WhatsApp."
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  const text = `To receive your CCS reflection summary on WhatsApp:\n\n1. Save this number: ${waStatus.from_number_display || "+1 415 523 8886"}\n2. Send it this message: join ${waStatus.sandbox_join_code || "<code>"}\n3. Wait for the confirmation\n\nThat's it — you're set for every session.`;
                  navigator.clipboard.writeText(text);
                  toast.success("Instructions copied — paste into a slide, WhatsApp group, or email");
                }}
                data-testid="copy-opt-in-instructions"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy instructions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Workshop selector */}
      <div className="ccs-card p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="text-xs uppercase tracking-wider text-[#C28A2E] font-bold md:mr-2">Active engagement</div>
        <Select value={activeWorkshopId} onValueChange={setActiveWorkshopId}>
          <SelectTrigger data-testid="fac-workshop-select" className="md:max-w-md"><SelectValue placeholder="Select a workshop…" /></SelectTrigger>
          <SelectContent>
            {workshops.map((w) => (
              <SelectItem key={w.id} value={w.id}>{w.name} · {w.client_name || "—"}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeWorkshop && (
          <div className="text-xs text-[#6B5944] md:ml-auto">
            {activeWorkshop.date} · {activeWorkshop.participants_count} participants · <strong>{activeWorkshop.status}</strong>
          </div>
        )}
        <Button
          onClick={generateReport}
          disabled={!activeWorkshopId || reportLoading}
          className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl hover:-translate-y-[1px] transition-all"
          data-testid="generate-report-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {reportLoading ? "Generating…" : "Generate Training Report"}
        </Button>
      </div>

      {loading ? <div className="text-center py-16 text-[#6B5944]">Loading…</div> : (
        <Tabs defaultValue="qr" className="w-full">
          <TabsList className="bg-[#EBE3D2]/50 rounded-xl flex-wrap h-auto">
            <TabsTrigger value="qr" data-testid="fac-tab-qr" className="rounded-lg"><QrCode className="w-3.5 h-3.5 mr-1.5" />QR Codes</TabsTrigger>
            <TabsTrigger value="reflections" data-testid="fac-tab-reflections" className="rounded-lg">
              Reflections
              <Badge variant="secondary" className="ml-1.5 bg-white text-[#6B5944]">{filteredReflections.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="feedback" data-testid="fac-tab-feedback" className="rounded-lg">
              Feedback
              {pendingCount > 0 && <Badge className="ml-1.5 bg-[#C28A2E] text-white text-[10px]">{pendingCount} pending</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* QR CODES TAB */}
          <TabsContent value="qr" className="mt-5">
            {!activeWorkshopId ? (
              <div className="text-center py-16 text-[#6B5944]">Select a workshop above to generate QR codes.</div>
            ) : (
              <>
                <div className="mb-5 ccs-card ccs-grain p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">How to use</div>
                  <p className="text-sm text-[#5D3A1F] mt-1.5">
                    Display <strong>QR Code 1</strong> at the end of each individual session (1, 2, 3, 4).
                    Display <strong>QR Code 2</strong> only at the end of Session 4 for end-of-programme feedback.
                    Download the PNGs to embed in slides or print as table tents.
                  </p>
                  <p className="text-sm text-[#5D3A1F] mt-2 leading-relaxed">
                    <strong className="text-[#C28A2E]">No phone? No WiFi?</strong> Tap <em>"Launch kiosk on this device"</em> below
                    to open the form on your own laptop/tablet and pass it around — it auto-resets between participants
                    and saves submissions locally if there's no internet, syncing as soon as you're back online.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4].map((n) => (
                    <QRTile
                      key={n}
                      url={`${SITE_BASE}/reflect/${activeWorkshopId}/${n}`}
                      kioskUrl={`${SITE_BASE}/reflect/${activeWorkshopId}/${n}?kiosk=1`}
                      label={`Session ${n} Reflection`}
                      sublabel="QR Code 1"
                      testid={`qr-reflect-${n}`}
                    />
                  ))}
                  <QRTile
                    url={`${SITE_BASE}/feedback/${activeWorkshopId}`}
                    kioskUrl={`${SITE_BASE}/feedback/${activeWorkshopId}?kiosk=1`}
                    label="End-of-Programme Feedback"
                    sublabel="QR Code 2 · Session 4 only"
                    testid="qr-feedback"
                  />
                  <QRTile
                    url={`${SITE_BASE}/testimonials`}
                    label="Public Testimonial Wall"
                    sublabel="Shareable bonus"
                    testid="qr-wall"
                  />
                </div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[1,2,3,4].map((n, i) => (
                    <div key={n} className="ccs-card p-4 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-[#6B5944]">Session {n}</div>
                      <div className="font-display text-2xl font-semibold mt-1 text-[#2A1810]">{sessionCounts[i]}</div>
                      <div className="text-xs text-[#6B5944]">reflections</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* REFLECTIONS TAB */}
          <TabsContent value="reflections" className="mt-5 space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-sm text-[#6B5944]">
                {filteredReflections.length} reflection{filteredReflections.length === 1 ? "" : "s"}
                {activeWorkshop && <> for <strong>{activeWorkshop.name}</strong></>}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setManualReflectionOpen(true)}
                  disabled={!activeWorkshopId}
                  className="border-[#C28A2E]/40 text-[#5D3A1F] hover:bg-[#C28A2E]/10"
                  data-testid="add-reflection-manually-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Add reflection
                </Button>
                <Button variant="outline" onClick={() => fetchAll()} data-testid="refresh-statuses-btn">
                  <Send className="w-4 h-4 mr-2" /> Refresh delivery
                </Button>
                <Button variant="outline" onClick={exportReflectionsCSV} disabled={filteredReflections.length === 0} data-testid="export-reflections-btn">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
            <div className="ccs-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#EBE3D2] hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] py-4">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Session</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Participant</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Dept</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">WhatsApp</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReflections.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B5944]">
                      No reflections yet. Share the QR codes with participants at the end of each session.
                    </TableCell></TableRow>
                  ) : filteredReflections.map((r) => (
                    <TableRow key={r.id} className="border-[#EBE3D2]" data-testid={`reflection-row-${r.id}`}>
                      <TableCell className="text-sm py-4">{(r.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell><Badge className="bg-[#5D3A1F]/10 text-[#5D3A1F]">Session {r.session_number}</Badge></TableCell>
                      <TableCell>
                        <div className="font-medium text-[#2A1810] flex items-center gap-1.5">
                          {r.full_name}
                          {r.is_first_time_participant && (
                            <Badge className="bg-[#C28A2E]/15 text-[#A6741F] text-[9px] py-0 px-1.5 h-4" data-testid={`first-time-${r.id}`}>
                              ✨ First time
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-[#6B5944]">{r.whatsapp_number}</div>
                      </TableCell>
                      <TableCell className="text-sm">{r.department}</TableCell>
                      <TableCell>
                        <Badge className={`${WA_STATUS_COLOR[r.whatsapp_status] || ""} text-[10px]`}>
                          <Send className="w-3 h-3 mr-1" /> {r.whatsapp_status || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setDetailR(r)} data-testid={`view-reflection-${r.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onDeleteReflection(r.id)}>
                          <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* FEEDBACK TAB */}
          <TabsContent value="feedback" className="mt-5 space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-sm text-[#6B5944]">{filteredFeedback.length} submission{filteredFeedback.length === 1 ? "" : "s"}</div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setManualFeedbackOpen(true)}
                  disabled={!activeWorkshopId}
                  className="border-[#C28A2E]/40 text-[#5D3A1F] hover:bg-[#C28A2E]/10"
                  data-testid="add-feedback-manually-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Add feedback
                </Button>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]" data-testid="fac-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending review</SelectItem>
                    <SelectItem value="approved">Approved (public)</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredFeedback.length === 0 ? (
                <div className="md:col-span-2 ccs-card p-12 text-center text-[#6B5944]">
                  No feedback yet. Share the end-of-programme QR code at Session 4.
                </div>
              ) : filteredFeedback.map((f) => (
                <div key={f.id} className="ccs-card ccs-card-hover p-5" data-testid={`feedback-card-${f.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={`${STATUS_COLOR[f.status] || ""} rounded-full font-medium px-3 capitalize`}>{f.status}</Badge>
                    <div className="flex gap-1 -mr-2">
                      <Button variant="ghost" size="icon" onClick={() => setModerate(f)} data-testid={`moderate-${f.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDeleteFeedback(f.id)}>
                        <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                      </Button>
                    </div>
                  </div>
                  <div className="font-display text-lg font-semibold text-[#2A1810]">{f.display_name}</div>
                  <div className="text-xs text-[#6B5944]">{f.organisation} · {(f.created_at || "").slice(0, 10)}</div>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= f.star_rating ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`} strokeWidth={1.5} />)}
                    <span className="ml-2 text-xs text-[#C28A2E] font-medium">{f.would_recommend}</span>
                  </div>
                  {f.enjoyed_most && <p className="text-sm text-[#5D3A1F] mt-3 italic line-clamp-3">"{f.enjoyed_most}"</p>}
                  <div className="mt-3 text-[10px] flex gap-1.5 items-center">
                    {f.share_publicly ? (
                      <Badge className="bg-[#6B8E5A]/15 text-[#6B8E5A]">Public OK</Badge>
                    ) : (
                      <Badge className="bg-[#6B5944]/15 text-[#6B5944]">Private</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}

      <ReflectionDetail r={detailR} onClose={() => setDetailR(null)} />
      <ModerateDialog item={moderate} onClose={() => setModerate(null)} onRefresh={fetchAll} />
      <ManualReflectionDialog
        open={manualReflectionOpen}
        onOpenChange={setManualReflectionOpen}
        workshop={activeWorkshop}
        defaultSession={1}
        onSubmitted={fetchAll}
      />
      <ManualFeedbackDialog
        open={manualFeedbackOpen}
        onOpenChange={setManualFeedbackOpen}
        workshop={activeWorkshop}
        onSubmitted={fetchAll}
      />

      {/* AI Post-Training Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="training-report-dialog">
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI Post-Training Report
            </div>
            <DialogTitle className="font-display text-2xl">
              {reportMeta?.workshop_name || activeWorkshop?.name || "Training Report"}
            </DialogTitle>
            {reportMeta && (
              <div className="text-xs text-[#6B5944] flex gap-3">
                <span>{reportMeta.client_name}</span>
                <span>·</span>
                <span>{reportMeta.reflections_count} reflections</span>
                <span>·</span>
                <span>{reportMeta.feedback_count} feedback entries</span>
              </div>
            )}
          </DialogHeader>
          {reportLoading ? (
            <div className="py-16 text-center">
              <div className="inline-flex items-center gap-2 text-[#6B5944]">
                <Sparkles className="w-4 h-4 text-[#C28A2E] animate-pulse" />
                Synthesizing reflections into a polished report… this may take 30-60s
              </div>
            </div>
          ) : (
            <div className="ccs-prose text-sm mt-2" data-testid="training-report-content">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed m-0">{reportContent}</pre>
            </div>
          )}
          <DialogFooter className="gap-2 mt-3">
            <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(reportContent); toast.success("Report copied"); }} disabled={!reportContent || reportLoading}>
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
            <Button onClick={downloadReport} disabled={!reportContent || reportLoading} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="download-report-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Download .md
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilitatorPage;
